import React, { useMemo, useState } from "react";
import { parseISO, isValid } from "date-fns";
import { Calendar as CalendarIcon, TrendingUp, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/* ---------- helpers ------------------------------------------------------- */
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function daysAgo(n) { const x = startOfDay(new Date()); x.setDate(x.getDate() - n); return x; }
function parseTaskDate(t) {
  const raw = t.date || t.created_at;
  if (!raw) return null;
  const d = typeof raw === "string" ? parseISO(raw) : new Date(raw);
  return isValid(d) ? d : null;
}
const PROJECT_FALLBACK_COLORS = [
  "#8B5CF6", "#EC4899", "#06B6D4", "#F59E0B", "#10B981",
  "#3B82F6", "#EF4444", "#A855F7", "#14B8A6", "#F97316",
];
function fallbackColor(name, i) {
  if (!name) return PROJECT_FALLBACK_COLORS[i % PROJECT_FALLBACK_COLORS.length];
  let h = 0;
  for (let k = 0; k < name.length; k++) h = (h * 31 + name.charCodeAt(k)) % 1000;
  return PROJECT_FALLBACK_COLORS[h % PROJECT_FALLBACK_COLORS.length];
}

/* ---------- component ----------------------------------------------------- */
export default function MarketingAnalytics({ tasks, projectColors }) {
  const [customOpen, setCustomOpen] = useState(false);
  const [customRange, setCustomRange] = useState({
    from: daysAgo(14).toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10),
  });

  const buckets = useMemo(() => {
    const now = startOfDay(new Date());
    const in7 = daysAgo(6);
    const in30 = daysAgo(29);
    const in90 = daysAgo(89);
    const cFrom = startOfDay(parseISO(customRange.from));
    const cTo = startOfDay(parseISO(customRange.to));
    const b = { w: [], m: [], q: [], c: [] };
    for (const t of tasks || []) {
      const d = parseTaskDate(t);
      if (!d) continue;
      if (d >= in7 && d <= now) b.w.push(t);
      if (d >= in30 && d <= now) b.m.push(t);
      if (d >= in90 && d <= now) b.q.push(t);
      if (d >= cFrom && d <= cTo) b.c.push(t);
    }
    return b;
  }, [tasks, customRange]);

  const splitBy = (list) => {
    const map = new Map();
    for (const t of list) {
      const key = t.project_name || "(no project)";
      map.set(key, (map.get(key) || 0) + 1);
    }
    return Array.from(map, ([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  };
  const colorFor = (name, i) => {
    if (name === "(no project)") return "#64748B";
    return (projectColors || {})[name] || fallbackColor(name, i);
  };

  const cards = [
    { key: "w", icon: TrendingUp, title: "Weekly",    subtitle: "Last 7 days",   tasks: buckets.w, accent: "from-sky-500 to-indigo-500" },
    { key: "m", icon: TrendingUp, title: "Monthly",   subtitle: "Last 30 days",  tasks: buckets.m, accent: "from-purple-500 to-fuchsia-500" },
    { key: "q", icon: TrendingUp, title: "Quarterly", subtitle: "Last 90 days",  tasks: buckets.q, accent: "from-amber-500 to-rose-500" },
    { key: "c", icon: CalendarIcon, title: "Selected Range", subtitle: `${customRange.from} → ${customRange.to}`, tasks: buckets.c, accent: "from-emerald-500 to-teal-500", custom: true },
  ];

  // Project split for the chart = whatever bucket the user is "looking at"
  // most actively. We pick the Monthly bucket by default and let the user
  // click a card to switch; stored in local state.
  const [activeCard, setActiveCard] = useState("m");
  const activeTasks = buckets[activeCard] || buckets.m;
  const activeTitle = (cards.find((c) => c.key === activeCard) || cards[1]).title;
  const split = useMemo(() => splitBy(activeTasks), [activeTasks]);
  const total = activeTasks.length || 1;

  return (
    <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-b from-slate-50/60 to-transparent" data-testid="mkt-analytics-bar">
      {/* KPI card row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {cards.map((c) => {
          const Icon = c.icon;
          const isActive = activeCard === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setActiveCard(c.key)}
              data-testid={`analytics-card-${c.key}`}
              className={`group relative overflow-hidden rounded-xl border px-4 py-3 text-left transition-all shadow-sm hover:shadow-md ${isActive ? "border-purple-300 ring-2 ring-purple-200 bg-white" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              {/* Gradient accent strip */}
              <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${c.accent}`} />
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{c.title}</div>
                  <div className="text-2xl md:text-3xl font-bold text-slate-900 mt-0.5 tabular-nums">{c.tasks.length}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">{c.subtitle}</div>
                </div>
                <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${c.accent} flex items-center justify-center shadow-sm shrink-0`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
              </div>
              {c.custom && (
                <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                  <Popover open={customOpen} onOpenChange={setCustomOpen}>
                    <PopoverTrigger asChild>
                      <button
                        data-testid="analytics-custom-range-btn"
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-purple-600 hover:text-purple-800 transition"
                      >
                        <CalendarIcon className="h-3 w-3" /> Change range <ChevronDown className="h-3 w-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-3" align="start">
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs">From</Label>
                          <Input type="date" value={customRange.from} onChange={(e) => setCustomRange({ ...customRange, from: e.target.value })} className="h-8 text-xs" data-testid="analytics-custom-from" />
                        </div>
                        <div>
                          <Label className="text-xs">To</Label>
                          <Input type="date" value={customRange.to} onChange={(e) => setCustomRange({ ...customRange, to: e.target.value })} className="h-8 text-xs" data-testid="analytics-custom-to" />
                        </div>
                        <Button size="sm" onClick={() => { setActiveCard("c"); setCustomOpen(false); }} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="analytics-custom-apply">Apply</Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Project split chart */}
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm" data-testid="mkt-analytics-split">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Orders by Project</div>
            <div className="text-sm text-slate-700 font-medium">{activeTitle} · <span className="text-slate-500">{activeTasks.length} orders</span></div>
          </div>
        </div>
        {split.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-400 italic">No orders in this range</div>
        ) : (
          <div className="space-y-1.5">
            {split.slice(0, 8).map((row, i) => {
              const pct = Math.round((row.count / total) * 100);
              const color = colorFor(row.name, i);
              return (
                <div key={row.name} className="group flex items-center gap-3 text-xs" data-testid={`analytics-split-row-${row.name}`}>
                  <div className="w-32 shrink-0 flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                    <span className="text-slate-700 truncate font-medium">{row.name}</span>
                  </div>
                  <div className="flex-1 h-6 rounded bg-slate-100 overflow-hidden relative">
                    <div className="h-full rounded transition-all" style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.9 }} />
                    <span className="absolute inset-y-0 right-2 flex items-center text-[10px] text-slate-700 font-semibold tabular-nums">{row.count} {row.count === 1 ? "order" : "orders"}</span>
                  </div>
                </div>
              );
            })}
            {split.length > 8 && (
              <div className="pt-1 text-[11px] text-slate-400 italic">+ {split.length - 8} more…</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

MarketingAnalytics.fallbackColor = fallbackColor;
export { fallbackColor };
